import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, UserPlus, Copy, AlertTriangle } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { OrgRole, ORG_ROLE_LABELS, ORG_ROLE_DESCRIPTIONS } from "@/hooks/useOrgMembers";

interface InviteOrgMemberModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  onSuccess: () => void;
  canAssignRole: OrgRole[];
  /**
   * Prefills the address when the flow is opened from the team roster's
   * "Invite them to Compaire" action. The administrator still reviews and sends
   * it themselves; nothing about this prop sends anything.
   */
  defaultEmail?: string;
}

const InviteOrgMemberModal = ({ open, onOpenChange, orgId, onSuccess, canAssignRole, defaultEmail }: InviteOrgMemberModalProps) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<OrgRole>("editor");
  const [sending, setSending] = useState(false);
  const [manualResult, setManualResult] = useState<{ email: string; signupLink: string } | null>(null);

  // Adopt the prefilled address each time the dialog opens, so reopening it for
  // a different person does not keep the previous one.
  useEffect(() => {
    if (open) setEmail(defaultEmail ?? "");
  }, [open, defaultEmail]);

  // Filter out super_admin from assignable roles
  const availableRoles = canAssignRole.filter(r => r !== "super_admin");

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link copied to clipboard!");
    } catch {
      toast.error("Failed to copy link.");
    }
  };

  const handleInvite = async () => {
    if (!email.trim()) return;
    setSending(true);
    setManualResult(null);
    try {
      const { data, error } = await supabase.functions.invoke("send-org-invite", {
        body: {
          email: email.trim(),
          role,
          orgId,
        },
      });

      // supabase-js puts non-2xx responses in `error.context`; recover the body.
      let body: any = data;
      if (error && (error as any).context) {
        try {
          body = await (error as any).context.json();
        } catch {
          // leave body as-is
        }
      }

      if (body?.success) {
        toast.success(`Invite sent to ${email.trim()}.`);
        setEmail("");
        setRole("editor");
        onOpenChange(false);
        onSuccess();
        return;
      }

      if (body?.signupLink) {
        // Email failed but invitation row exists — surface link for manual delivery.
        setManualResult({ email: email.trim(), signupLink: body.signupLink });
        toast.error(body.error || "Email failed. Copy the link below to share manually.");
        onSuccess();
        return;
      }

      toast.error(body?.error || error?.message || "Failed to invite member");
    } catch (err: any) {
      toast.error(err?.message || "Failed to invite member");
    } finally {
      setSending(false);
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setEmail("");
      setRole("editor");
      setManualResult(null);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Invite organization member</DialogTitle>
          <DialogDescription>
            Enter an email address to send an invitation to join your organization.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="member-email">Email address</Label>
            <Input
              id="member-email"
              type="email"
              placeholder="member@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Role</Label>
            <Select value={role} onValueChange={(v) => setRole(v as OrgRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-popover">
                {availableRoles.map((r) => (
                  <SelectItem key={r} value={r}>
                    <div>
                      <span className="font-medium">{ORG_ROLE_LABELS[r]}</span>
                      <span className="text-xs text-muted-foreground ml-2">— {ORG_ROLE_DESCRIPTIONS[r]}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {manualResult && (
            <div className="space-y-2 border-t pt-4 text-sm">
              <div className="flex items-start gap-2 text-amber-700">
                <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                <span className="font-medium">
                  Email failed for {manualResult.email} — copy the link to share manually:
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 rounded-md border bg-muted/40 px-2 py-1.5">
                <span className="truncate text-xs">{manualResult.email}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => copyLink(manualResult.signupLink)}
                  className="h-7 gap-1 px-2 text-xs"
                >
                  <Copy className="h-3 w-3" />
                  Copy link
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => handleClose(false)}>
            {manualResult ? "Close" : "Cancel"}
          </Button>
          <Button onClick={handleInvite} disabled={sending || !email.trim()} className="gap-2">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
            {sending ? "Sending..." : "Send invite"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InviteOrgMemberModal;
